import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  forwardRef,
} from 'react'
import { Link, useLocation } from 'react-router-dom'
import cn from 'classnames'
import ScrollMenu from 'react-horizontal-scrolling-menu'
import pickBy from 'lodash.pickby'
import { ModalContext } from 'modaaals'

import SelectionContext from '../../context/SelectionContext'
import TrackContext from '../../context/TrackContext'
import { getTracksInfo } from '../../api'
import Logo from '../LogoPixedenMusic'
import { ProfileNavItem } from '../Layout'
import * as Icon from '../Icons'
import { Loader as LoaderIcon } from '../Icons'

import FilterItem from './FilterItem'
import TracksWarpper from './TracksWrapper'
import SelectedItems from './SelectedItems'

import GENRE_ITEMS from '../filters/genre'
import VOCALS_ITEMS from '../filters/vocals'
import TEMPO_ITEMS from '../filters/tempo'
import LANGUAGE_ITEMS from '../filters/language'
import ARTIST_ITEMS from '../filters/artist'

import styles from './app.module.css'

const PER_PAGE = 100

function MusicApp({ forwardedRef }) {
  const searchInputRef = useRef(null)

  const infiniteLoaderRef = useRef(null)
  let location = useLocation()

  const [currentPage, setCurrentPage] = useState(1)
  const [inputValue, setInputValue] = useState('')

  const { selectionState, setSelectionState } = useContext(SelectionContext)
  const { openModal } = useContext(ModalContext)
  const { playerTrack, setPlayerTrack, tracks, filterTracks, query, setQuery } =
    useContext(TrackContext)

  const [filterTab, setFilterTab] = useState(null)
  const [itemsToUnselect, setItemsToUnselect] = useState([])

  const [totalHits, setTotalHits] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  function scrollTop() {
    if (infiniteLoaderRef.current) {
      infiniteLoaderRef.current._listRef.scrollToItem(0)
    }
  }

  useEffect(() => {
    let canceled = false

    function search() {
      setIsLoading(true)
      const url = new URL(process.env.REACT_APP_SEARCH_URI)
      Object.keys(query)
        .filter((key) => query[key])
        .forEach((key) => {
          let value = query[key]
          if (Array.isArray(value)) {
            value = value.join(',')
          }
          url.searchParams.append(key, value)
        })

      url.searchParams.append('size', PER_PAGE)
      url.searchParams.append('offset', (currentPage - 1) * PER_PAGE)

      fetch(url)
        .then((res) => res.json())
        .then(({ hits }) => {
          if (!canceled) {
            if (hits.hits.length > 0) {
              setTotalHits(hits.total.value)
              filterTracks((tracks) => {
                return [
                  ...(currentPage > 1 ? tracks : []),
                  ...hits.hits.map((tr) => ({
                    id: tr._id,
                    ...tr._source,
                  })),
                ]
              })
            } else {
              setTotalHits(0)
              filterTracks([])
            }
            setIsLoading(false)
          }
        })
        .catch((err) => {
          console.log({ err })
          setIsLoading(false)
        })
    }

    if (query) {
      search()
    }

    return () => {
      canceled = true
    }
  }, [query, currentPage, filterTracks])

  useEffect(() => {
    const { state, hash, search } = location

    if (state && state.artist) {
      const { artist } = location.state
      setQuery({ artist })
    }

    const hsh = hash.match(/#(.*)/)
    if (hsh && typeof hsh[1] === 'string' && hsh[1].length > 0) {
      const queryParsed = JSON.parse(atob(hsh[1]))
      setQuery(queryParsed)
      if (queryParsed.term) {
        setInputValue(queryParsed.term)
      }
    }

    if (search) {
      getTracksInfo(search).then(({ hits: { hits } }) => {
        let newItems = {}
        for (const { _source } of hits) {
          const { artist, image, name, slug } = _source
          newItems[slug] = { artist, image, name, slug }
        }
        setSelectionState((selection) => ({ ...selection, ...newItems }))
      })
    }
    return () => {
      setQuery({
        term: null,
        artist: null,
        genre: null,
        vocals: null,
        tempo: null,
        language: null,
      })
    }
  }, [location, setQuery, setSelectionState])

  const queryNotEmpty = Object.keys(query).reduce((acc, next) => {
    const val = query[next]
    if (acc) {
      return true
    } else {
      if (val) {
        if (Array.isArray(val)) {
          return val.length === 0 ? false : true
        } else {
          return val === '' ? false : true
        }
      } else {
        return false
      }
    }
  }, false)

  function toggleSelection({ slug, ...rest }) {
    setSelectionState((selection) => {
      if (selection[slug]) {
        return pickBy(selection, (val, key) => key !== slug)
      } else {
        return {
          [slug]: {
            slug,
            ...rest,
          },
          ...selection,
        }
      }
    })
  }

  function Tag({ type, name }) {
    return (
      <li className={styles.tag} onClick={() => filter(type, name)}>
        <span className={styles.tagType}>
          {type === 'artist' && <Icon.User className={styles.tagIconArtist} />}
          {type === 'genre' && <Icon.Disc className={styles.tagIconDisc} />}
          {type === 'vocals' && <Icon.Mic className={styles.tagIconMic} />}
          {type === 'tempo' && (
            <Icon.Equalizer className={styles.tagIconTempo} />
          )}
          {type === 'language' && (
            <Icon.Message className={styles.tagIconMessage} />
          )}
        </span>
        <span className={styles.tagCloseBtn}>
          <Icon.CloseCircle />
        </span>
        {name}
      </li>
    )
  }

  function onSubmitSearch(e) {
    e.preventDefault()
    if (inputValue) {
      setCurrentPage(1)
      setQuery((query) => ({
        ...query,
        term: inputValue,
      }))
    }
  }

  const inputActive = inputValue !== '' && inputValue === query.term

  function filter(type, item, toggle = true) {
    const queryItems = query[type]
    const bypass = !toggle && query[type] && query[type]?.includes(item)
    if (!bypass) {
      setCurrentPage(1)
      setQuery((query) => ({
        ...query,
        [type]: [
          ...(queryItems && queryItems.includes(item) && toggle
            ? queryItems.filter((i) => i !== item)
            : [...new Set([...(queryItems ? queryItems : []), item])]),
        ],
      }))
      scrollTop()
    }
  }

  return (
    <>
      <section className={styles.searchBar}>
        <div className={cn(styles.containerOut, styles.searchBarContent)}>
          <Link className={styles.logo} to="/">
            <Logo />
          </Link>
          <form className={styles.searchForm} onSubmit={onSubmitSearch}>
            <label>
              <Icon.Search className={styles.searchFormIcon} />
              <input
                className={cn(styles.searchInput, {
                  [styles.active]: inputActive,
                })}
                placeholder="&nbsp;Search for Artist or Song"
                onChange={(e) => setInputValue(e.target.value)}
                value={inputValue}
                ref={searchInputRef}
                disabled={inputActive}
              />
            </label>
            {inputActive && (
              <button
                type="button"
                className={styles.searchCleanBtn}
                onClick={(e) => {
                  e.preventDefault()
                  setCurrentPage(1)
                  setInputValue('')
                  setQuery((query) => ({
                    ...query,
                    term: '',
                  }))
                  setTimeout(() => searchInputRef.current.focus(), 1)
                }}
              >
                <Icon.CloseCircle className={styles.searchCleanBtnIcon} />
              </button>
            )}
          </form>
          <ScrollMenu
            menuClass={styles.tags}
            dragging={false}
            itemStyle={{ outline: 0 }}
            wheel={false}
            arrowClass={styles.tagsArrowWrapper}
            arrowLeft={<div className={styles.tagsArrow} />}
            arrowRight={
              <div className={cn(styles.tagsArrow, styles.tagsArrowLeft)} />
            }
            alignCenter={false}
            hideArrows
            hideSingleArrow
            arrowDisabledClass={styles.tagsArrowDisabled}
            data={[
              ...(query.artist && query.artist.length > 0
                ? query.artist.map((artist) => (
                    <Tag type="artist" name={artist} key={artist} />
                  ))
                : []),
              ...(query.genre && query.genre.length > 0
                ? query.genre.map((genre) => (
                    <Tag type="genre" name={genre} key={genre} />
                  ))
                : []),
              ...(query.tempo && query.tempo.length > 0
                ? query.tempo.map((tempo) => (
                    <Tag type="tempo" name={tempo} key={tempo} />
                  ))
                : []),
              ...(query.language && query.language.length > 0
                ? query.language.map((language) => (
                    <Tag type="language" name={language} key={language} />
                  ))
                : []),
              ...(query.vocals && query.vocals.length > 0
                ? query.vocals.map((vocals) => (
                    <Tag type="vocals" name={vocals} key={vocals} />
                  ))
                : []),
            ]}
          />

          <div className={styles.navWrapper}>
            {queryNotEmpty ? (
              <button
                className={styles.shareButton}
                onClick={() => {
                  openModal({
                    type: 'share',
                    urlParams: `#${btoa(JSON.stringify(query))}`,
                  })
                }}
              >
                Share
              </button>
            ) : null}

            <ProfileNavItem
              className={styles.profileLink}
              anchorClassName={styles.profileLinkAnchor}
            />
          </div>
        </div>
      </section>
      <section className={styles.tracks}>
        <div
          className={cn(styles.tracksList, styles.containerIn, {
            [styles.hideColumn]: !filterTab,
          })}
        >
          <div className={cn(styles.tracksListColumn)}>
            <h3
              className={cn(
                styles.tracksListTitle,
                styles.tracksListTitleFilters
              )}
            >
              Filters
            </h3>
            <div
              className={cn(
                styles.tracksFilterList,
                styles.trackFilterSections
              )}
            >
              {FILTER_SECTIONS.map(({ name, icon }) => (
                <button
                  onClick={() =>
                    setFilterTab((tab) =>
                      !tab ? name : tab !== name ? name : null
                    )
                  }
                  className={cn(styles.tracksFilterItem, styles.tracksButton, {
                    [styles.active]: name === filterTab,
                  })}
                  key={name}
                >
                  {icon}
                  <div
                    className={styles.tracksFilterItemText}
                    style={{
                      display: 'inline-block',
                      verticalAlign: 'middle',
                    }}
                  >
                    {name}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div
            className={cn(styles.tracksListColumn, {
              [styles.hideColumn]: !filterTab,
            })}
          >
            <h3
              className={styles.tracksListTitle}
              style={{
                visibility: 'hidden',
                fontSize: 21,
                marginTop: 28,
                marginBottom: 22,
              }}
            >
              Filter selection
            </h3>
            <ul
              className={cn(
                styles.tracksFilterList,
                styles.trackFilterSelection
              )}
              style={{
                overscrollBehavior: 'contain',
                paddingTop: 10,
              }}
            >
              {filterTab === 'Genre' &&
                GENRE_ITEMS.sort().map((item) => (
                  <FilterItem
                    item={item}
                    queryItems={query['genre']}
                    key={item}
                    onClick={() => filter('genre', item)}
                  />
                ))}
              {filterTab === 'Vocals' &&
                VOCALS_ITEMS.map((item) => (
                  <FilterItem
                    item={item}
                    onClick={() => filter('vocals', item)}
                    queryItems={query['vocals']}
                    key={item}
                  />
                ))}
              {filterTab === 'Tempo' &&
                TEMPO_ITEMS.map((item) => (
                  <FilterItem
                    item={item}
                    onClick={() => filter('tempo', item)}
                    queryItems={query['tempo']}
                    key={item}
                  />
                ))}
              {filterTab === 'Language' &&
                LANGUAGE_ITEMS.map((item) => (
                  <FilterItem
                    item={item}
                    onClick={() => filter('language', item)}
                    queryItems={query['language']}
                    key={item}
                  />
                ))}
              {filterTab === 'Artist' &&
                ARTIST_ITEMS.sort().map((item) => (
                  <FilterItem
                    item={item}
                    onClick={() => filter('artist', item)}
                    queryItems={query['artist']}
                    key={item}
                  />
                ))}
            </ul>
          </div>
          <div
            className={styles.tracksListColumn}
            style={{
              paddingLeft: 21,
            }}
          >
            <h3
              className={styles.tracksListTitle}
              style={{
                marginLeft: 0,
                marginBottom: 28,
              }}
            >
              <span className={styles.tracksListTitleIcon}>
                <Icon.TracksList />
              </span>
              Tracks
            </h3>
            {tracks.length > 0 ? (
              <TracksWarpper
                hasNextPage={tracks.length < totalHits}
                isNextPageLoading={isLoading}
                items={tracks}
                loadNextPage={() => setCurrentPage((page) => page + 1)}
                playerTrack={playerTrack}
                setPlayerTrack={setPlayerTrack}
                selectionState={selectionState}
                toggleSelection={toggleSelection}
                filter={filter}
                infiniteLoaderRef={infiniteLoaderRef}
              />
            ) : (
              <div
                className={cn(
                  styles.tracksPlayerList,
                  styles.tracksPlayerListEmpty
                )}
                style={{ height: 'calc(100vh - 346px)' }}
              >
                {isLoading ? (
                  <div
                    className={styles.tracksLoader}
                    style={{ alignItems: 'center' }}
                  >
                    <LoaderIcon className={styles.loaderIcon} />
                  </div>
                ) : (
                  <p className={styles.tracksPlayerListEmptyText}>
                    No results for your Search and Filters. <br />
                    Try removing tags, changing the search term, or{' '}
                    <button
                      className={styles.link}
                      onClick={() => {
                        setQuery({
                          term: null,
                          artist: null,
                          genre: null,
                          vocals: null,
                          tempo: null,
                          language: null,
                        })
                        setInputValue('')
                        setCurrentPage(1)
                      }}
                    >
                      reset
                    </button>
                    .
                  </p>
                )}
              </div>
            )}
          </div>
          <div
            className={cn(
              styles.tracksListColumn,
              styles.tracksListColumnSelectionPlaylist
            )}
          >
            <div className={styles.selectionSubColumn}>
              <h3
                className={cn(
                  styles.tracksListTitle,
                  styles.tracksListTitleSelection
                )}
              >
                <span
                  className={styles.tracksListTitleIcon}
                  style={{
                    width: '.95em',
                    height: '.95em',
                    marginTop: '-.2em',
                    flexShrink: 0,
                  }}
                >
                  <Icon.SelectionList />
                </span>
                <span style={{ flexShrink: 0 }}>My Selection</span>
                {Object.keys(selectionState).length > 0 && (
                  <button
                    className={cn(
                      styles.tracksMainButton,
                      styles.tracksSelectAllButton,
                      { [styles.isUnselect]: itemsToUnselect.length > 0 }
                    )}
                    onClick={() => {
                      setItemsToUnselect((items) =>
                        items.length > 0 ? [] : Object.keys(selectionState)
                      )
                    }}
                  >
                    {itemsToUnselect.length > 0 ? (
                      'Unselect'
                    ) : (
                      <>
                        Select All
                        <Icon.Check
                          className={styles.tracksSelectAllButtonIcon}
                        />
                      </>
                    )}
                  </button>
                )}
              </h3>
              <SelectedItems
                selectionState={selectionState}
                itemsToUnselect={itemsToUnselect}
                setItemsToUnselect={setItemsToUnselect}
                setPlayerTrack={setPlayerTrack}
                playerTrack={playerTrack}
                setCurrentPage={setCurrentPage}
                query={query}
                setQuery={setQuery}
              />
              <div className={styles.selectionActions}>
                <button
                  className={styles.tracksQuoteButton}
                  onClick={() => {
                    if (Object.keys(selectionState).length === 0) {
                      openModal({
                        type: 'supervision',
                        asEmptyQuote: true,
                        fromApp: true,
                      })
                    } else {
                      openModal({
                        type: 'quote',
                        selectionState,
                        fromApp: true,
                      })
                    }
                  }}
                >
                  <Icon.Quote className={styles.darkButtonIcon} />
                  Send for Quote
                </button>
                {Object.keys(selectionState).length > 0 && (
                  <button
                    className={styles.tracksShareButton}
                    onClick={() => {
                      openModal({
                        type: 'share',
                        kind: 'selection',
                        urlParams: `?selection=${Object.keys(
                          selectionState
                        ).join(',')}`,
                      })
                    }}
                    style={{
                      marginLeft: '0.5em',
                      marginRight: 'auto',
                    }}
                    title="Share Selection"
                  >
                    <Icon.Share />
                  </button>
                )}
                {itemsToUnselect.length > 0 ? (
                  <button
                    className={cn(
                      styles.tracksMainButton,
                      styles.tracksRemoveSelectedButton
                    )}
                    onClick={() => {
                      setSelectionState((selection) => {
                        return pickBy(
                          selection,
                          (val, key) => !itemsToUnselect.includes(key)
                        )
                      })
                      setItemsToUnselect([])
                    }}
                  >
                    Remove
                    <Icon.Close
                      className={styles.tracksRemoveSelectedButtonIcon}
                    />
                  </button>
                ) : null}
              </div>
            </div>
            <div className={styles.findMySongSection}>
              <div>
                <h3
                  className={styles.tracksListTitle}
                  style={{
                    fontSize: 21,
                    fontWeight: 'var(--f-black)',
                    textTransform: 'uppercase',
                    margin: 0,
                    marginBottom: 8,
                  }}
                >
                  Supervision
                </h3>
                <p
                  style={{
                    color: '#737377',
                    fontSize: 16,
                    margin: 0,
                    letterSpacing: -0.3,
                  }}
                >
                  Ask us to find the best song for your project.
                </p>
              </div>
              <div className={styles.findMySongButtonSection}>
                <button
                  className={cn(
                    styles.tracksMainButton,
                    styles.tracksSupervisionBtn
                  )}
                  onClick={() =>
                    openModal({
                      type: 'supervision',
                      fromApp: true,
                    })
                  }
                >
                  <Icon.Search />
                  Find My Song
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default forwardRef((props, ref) => (
  <MusicApp {...props} forwardedRef={ref} />
))

const FILTER_SECTIONS = [
  { name: 'Genre', icon: <Icon.Disc className={styles.tracksFiltersIcon} /> },
  { name: 'Vocals', icon: <Icon.Mic className={styles.tracksFiltersIcon} /> },
  {
    name: 'Tempo',
    icon: <Icon.Equalizer className={styles.tracksFiltersIcon} />,
  },
  {
    name: 'Language',
    icon: <Icon.Message className={styles.tracksFiltersIcon} />,
  },
  { name: 'Artist', icon: <Icon.User className={styles.tracksFiltersIcon} /> },
]
